import { CashRequestMiddleware } from './types'

interface BrowserAPIs {
  navigator?: {
    platform: string
    userAgent: string
    hardwareConcurrency: number
    deviceMemory?: number
    language: string
    maxTouchPoints: number
    connection?: {
      effectiveType?: string
      downlink?: number
      rtt?: number
    }
    plugins?: {
      length: number
      item: (index: number) => Plugin | null
    }
    doNotTrack?: string | null
    cookieEnabled?: boolean
  }
  screen?: {
    colorDepth: number
    orientation: {
      type: string
    }
    pixelDepth: number
  }
  window?: {
    devicePixelRatio: number
    applePay?: unknown
    google?: {
      pay?: {
        api?: {
          PaymentsClient?: unknown
        }
      }
    }
    AudioContext?: {
      new (contextOptions?: AudioContextOptions): AudioContext
    }
    webkitAudioContext?: {
      new (contextOptions?: AudioContextOptions): AudioContext
    }
    localStorage?: Storage
    indexedDB?: IDBFactory
    performance?: Performance
  }
  document?: {
    createElement: typeof document.createElement
  }
}

export const getData = ({ navigator, screen, window, document }: BrowserAPIs): string => {
  return JSON.stringify([
    navigator?.platform,
    navigator?.userAgent,
    navigator?.maxTouchPoints,
    navigator?.hardwareConcurrency,
    navigator?.deviceMemory,
    navigator?.maxTouchPoints,
    navigator?.language,
    navigator?.cookieEnabled,
    navigator?.doNotTrack,
    navigator?.plugins?.length,
    navigator?.connection?.effectiveType || '',
    navigator?.connection?.downlink || '',
    navigator?.connection?.rtt || '',

    screen?.colorDepth,
    screen?.pixelDepth,
    screen?.orientation?.type,

    window?.devicePixelRatio,
    Intl.DateTimeFormat().resolvedOptions().timeZone,
    !!window?.applePay,
    !!window?.google?.pay?.api?.PaymentsClient,

    // Feature detection
    window && 'localStorage' in window,
    window && 'indexedDB' in window,
    window && ('AudioContext' in window || 'webkitAudioContext' in window),
    navigator && 'credentials' in navigator,

    // Time zone and locale info
    new Date().getTimezoneOffset(),
    Intl.DateTimeFormat().resolvedOptions().calendar,
    Intl.DateTimeFormat().resolvedOptions().numberingSystem,

    // WebGL fingerprint
    getWGLF(document),

    // Audio fingerprint
    getAF(window),

    // Font detection (limited set)
    getFF(document),
  ])
}

// Enhanced WebGL Fingerprint with additional parameters
const getWGLF = (document: BrowserAPIs['document']): string | null => {
  const canvas = document?.createElement('canvas')
  const gl = (canvas?.getContext('webgl') || canvas?.getContext('experimental-webgl')) as WebGLRenderingContext | null

  if (!gl) return null

  const parameters = [
    'MAX_COMBINED_TEXTURE_IMAGE_UNITS',
    'MAX_CUBE_MAP_TEXTURE_SIZE',
    'MAX_FRAGMENT_UNIFORM_VECTORS',
    'MAX_RENDERBUFFER_SIZE',
    'MAX_TEXTURE_IMAGE_UNITS',
    'MAX_TEXTURE_SIZE',
    'MAX_VERTEX_ATTRIBS',
    'MAX_VERTEX_UNIFORM_VECTORS',
    'MAX_VIEWPORT_DIMS',
    'ALPHA_BITS',
    'BLUE_BITS',
    'GREEN_BITS',
    'RED_BITS',
    'DEPTH_BITS',
    'STENCIL_BITS',
  ].reduce(
    (acc, key) => ({
      ...acc,
      [key]: gl.getParameter(gl[key as keyof WebGLRenderingContext] as GLenum),
    }),
    {}
  )

  const extensions = gl.getSupportedExtensions()

  return JSON.stringify([
    gl.getParameter(gl.VENDOR),
    gl.getParameter(gl.RENDERER),
    gl.getParameter(gl.VERSION),
    parameters,
    extensions?.length, // Only store the count of supported extensions
  ])
}

// Enhanced Audio Fingerprint
const getAF = (window: BrowserAPIs['window']): string | null => {
  const createAudioContext = window?.AudioContext || window?.webkitAudioContext
  if (!createAudioContext) return null

  const audioContext = new createAudioContext()
  const oscillator = audioContext.createOscillator()
  const analyser = audioContext.createAnalyser()
  const gainNode = audioContext.createGain()

  const response = [
    audioContext.sampleRate,
    analyser.frequencyBinCount,
    audioContext.baseLatency || 0,
    oscillator.frequency.value,
    gainNode.gain.value,
  ].join(',')

  audioContext.close()
  return response
}

// Font Detection (using a limited set of fonts)
const getFF = (document: BrowserAPIs['document']): string[] => {
  const baseFonts = ['monospace', 'sans-serif', 'serif']
  const testString = 'mmmmmmmmmmlli'
  const testSize = '72px'
  const canvas = document?.createElement('canvas')
  const context = canvas?.getContext('2d')
  if (!context) return []

  const getWidth = (fontFamily: string): number => {
    context.font = `${testSize} ${fontFamily}`
    return context.measureText(testString).width
  }

  const fontList = ['Arial', 'Times New Roman', 'Courier New', 'Helvetica', 'Calibri', 'Verdana', 'Georgia', 'Tahoma']

  const baseWidths = baseFonts.map(getWidth)
  const detected = fontList.filter(font => {
    const widths = baseFonts.map(baseFont => getWidth(`${font}, ${baseFont}`))
    return widths.some((width, index) => width !== baseWidths[index])
  })

  return detected
}

// Generate Token
export const generateCBF = async (input: string, crypto?: Crypto): Promise<string | null> => {
  const encoder = new TextEncoder()
  const data = encoder.encode(input)
  const hashBuffer = await crypto?.subtle?.digest('SHA-256', data)

  if (!hashBuffer) {
    return null
  }

  const hashArray = Array.from(new Uint8Array(hashBuffer))
  const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('')

  if (hashHex == '') {
    return null
  }

  const bigInt = BigInt(`0x${hashHex}`)
  const base36 = bigInt.toString(36).toLowerCase()
  return `web-${base36}`
}
const canGenerateCBF = typeof crypto === 'undefined' || crypto?.subtle != null
const cfb = generateCBF(
  getData({
    navigator: typeof navigator === 'undefined' ? undefined : navigator,
    screen: typeof screen === 'undefined' ? undefined : screen,
    window: typeof window === 'undefined' ? undefined : (window as BrowserAPIs['window']),
    document: typeof document === 'undefined' ? undefined : document,
  }),
  typeof crypto === 'undefined' ? undefined : crypto
)
// Middleware
export const cbfRequest: CashRequestMiddleware = async request => {
  const value = canGenerateCBF ? await cfb : null
  if (value != null) {
    request.headers = {
      ...request.headers,
      'X-CBF': value,
    }
  }
  return request
}
